import { z } from 'zod';

/**
 * The authorization vocabulary the browser and the API share.
 *
 * What ships here is permission strings: a role maps to a set of them, and a
 * check asks whether the session holds one. Architecture/06 describes a
 * different model — `(action, subject)` rules evaluated by one CASL ability on
 * both sides of the wire (decision 0014) — which is the target and is not
 * built. 06's "Guards — what is still open" table carries the migration. Until
 * it happens this file, not 06, is what a permission actually is.
 *
 * Roles are coarse and few. They are granted in Google Workspace — a user's
 * groups arrive in the SAML assertion and the auth function maps them to roles
 * when it builds the session (decision 0015). Nothing downstream knows where a
 * role came from: permission checks read the session's `permissions`, and this
 * table is the one place a role becomes permissions.
 */
export const roleSchema = z.enum(['admin', 'chief_of_staff', 'attorney', 'paralegal']);
export type Role = z.output<typeof roleSchema>;
export const ROLES: readonly Role[] = roleSchema.options;

/**
 * Permission strings, `<resource>:<action>`. The set is a placeholder until the
 * front-of-house scope in architecture/01 is written down; adding one is a row
 * here and a row in the table below, nowhere else.
 */
export const permissionSchema = z.enum([
  'matter:read',
  'matter:write',
  'document:read',
  'document:download',
  'company:manage',
  'user:manage',
]);
export type Permission = z.output<typeof permissionSchema>;

const PERMISSIONS_BY_ROLE: Readonly<Record<Role, readonly Permission[]>> = {
  admin: [
    'matter:read',
    'matter:write',
    'document:read',
    'document:download',
    'company:manage',
    'user:manage',
  ],
  chief_of_staff: [
    'matter:read',
    'matter:write',
    'document:read',
    'document:download',
    'company:manage',
  ],
  attorney: ['matter:read', 'matter:write', 'document:read', 'document:download'],
  paralegal: ['matter:read', 'matter:write', 'document:read', 'document:download'],
};

/** The union of every role's permissions, deduplicated and in the enum's order. No roles, no permissions. */
export function permissionsForRoles(roles: readonly Role[]): readonly Permission[] {
  const granted = new Set<Permission>();
  for (const role of roles) {
    for (const permission of PERMISSIONS_BY_ROLE[role]) granted.add(permission);
  }
  return permissionSchema.options.filter((permission) => granted.has(permission));
}

/**
 * What `/api/session` answers (architecture/04 — "useSession is the only source
 * of identity in the browser"). Parsed by the browser, produced by the auth
 * function; a drift fails loudly on the browser side.
 */
export const sessionSchema = z.object({
  user: z.object({
    id: z.string().min(1),
    email: z.string().optional(),
    name: z.string().optional(),
  }),
  roles: z.array(roleSchema),
  companies: z.array(z.object({ id: z.string(), name: z.string() })),
  permissions: z.array(permissionSchema),
});

export type Session = z.output<typeof sessionSchema>;
